//==========================================
//⚠️ Keep index files under 100 lines!
//Split logic into submodules as needed.
//==========================================
import { BreadcrumbItem, Breadcrumbs } from "@vds/breadcrumbs";
import { Icon } from "@vds/icons";
import { DropdownSelect } from "@vds/selects";
import { Tooltip } from "@mui/material";
import { Body, Title } from "@vds/typography";
import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link, useNavigate } from "react-router-dom";
import CustomHOCTable from "../../global/components/Tables/CustomHOCTable";
import { useWindowSize } from "../../global/hooks/viewportHooks";
import { breakpoints } from "../../styles/breakpoints";
import { getLicenseOveruseState, getLicenseTypeDisplayed } from "../../global/utility/Utils";
import { fetchBillingAccountsLicenses, fetchLicenses } from "../../store/actions/license-actions";
import { resetTableFilterState } from "../../store/actions";
import {
  LICENSE_ACTION_TYPES,
  LICENSE_ORIGIN,
  LICENSE_STATUS,
  LICENSE_UI_TEXT,
  LICENSE_LABELS,
  licensesMessage,
  isV2Sku,
  DROPDOWN_DEFAULT_WIDTH
} from "../../global/constants/licensesConstants";
import "../../styles/licenseOveruseTooltip.scss";
import "./Licenses.scss";
import { Badge } from "@vds/badges";

function Licenses() {
  const licenses = useSelector((state) => state.licenseReducer.licenses || []); 
  const { fovList, cameraList } = useSelector((state) => state.deviceReducer);
  const ReducerbillingAccounts = useSelector((state) => state.licenseReducer.billingAccounts);
  const billingAccountsLoaded = useSelector((state) => state.licenseReducer.billingAccountsLoaded);
  const activeSession = useSelector((state) => state.sessionReducer.activeSession.activeSession);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { width: viewportWidth } = useWindowSize();
  const isMobile = viewportWidth !== undefined ? viewportWidth < parseInt(breakpoints.tablet) : false;

  useEffect(() => {
    if (!billingAccountsLoaded) {
      dispatch(fetchBillingAccountsLicenses());
    }
    // Reset the Licenses table filter state when returning to index page
    dispatch(resetTableFilterState({ pageIdentifier: "Licenses" }));
  }, [dispatch]);

  const moveToTop = (arr, val) => (arr.includes(val) ? [val, ...arr.filter((v) => v !== val)] : arr);

  let billingAccounts = moveToTop(ReducerbillingAccounts, activeSession.accountId);
  const firstBillingAcc = billingAccounts?.[0];

  const [selectedBillingAcc, setSelectedBillingAcc] = useState(firstBillingAcc);

  useEffect(() => {
    if (firstBillingAcc) {
      dispatch(fetchLicenses({"accountId": firstBillingAcc, "origin":LICENSE_ORIGIN.LICENSE}));
    }
  }, [dispatch, firstBillingAcc]);

  const handleBillingAccountChange = (e) => {
    const accountId = e.target.value;
    setSelectedBillingAcc(accountId);
    dispatch(fetchLicenses({"accountId": accountId, "origin":LICENSE_ORIGIN.LICENSE}));
  };

  const isGracePeriodExpired = useMemo(() => {
    if (!licenses || !Array.isArray(licenses)) return false;
    return licenses.some((lic) => getLicenseOveruseState(lic.overusestartedon) === LICENSE_STATUS.EXPIRED);
  }, [licenses]);

  const getSiteCount = (licenseSKU) => {
    const licensedfovs = fovList
      .filter((f) => {
        return f?.licenses != undefined && f?.licenses?.find((l) => l == licenseSKU);
      })
      .map((m) => {
        return m?.parentdeviceid;
      });

    const siteidsfromcamera = cameraList.filter((g) => {
      return licensedfovs.indexOf(g.id) > -1;
    });
    const t = siteidsfromcamera.map((g) => {
      return g?.customdata?.location?.site?.tag;
    });

    const siteListWithLicense = [...new Set(t)];

    const siteText = siteListWithLicense.length <= 1 ? " site" : " sites";

    return siteListWithLicense.length + siteText;
  };

  // licenses is already vision-only (filtered at the saga source).
  const dataList = licenses?.map((license, index) => ({
    key: index.toString(),
    ...license,
    license: license.sku,
    purchased: license.licenses,
    used: license.usedlicenses,
    available: license.availablelicenses,
    sites: getSiteCount(license.sku),
    overusestartedon: license.overusestartedon
  }));

  const columns = [
    {
      title: () => (
        <span className="licenses-table-header-title">
          <Title size="small" bold>
            {LICENSE_UI_TEXT.LICENSE}
          </Title>
        </span>
      ),
      dataIndex: "license",
      key: "license",
      width: "20%",
      render: (text, record) => {
        const licenseVersion = isV2Sku(record?.sku) ? "V2" : "V1";
        return (
          <span className="licenses-license-name-cell">
            <span
              className="license-details-table-link"
              data-testid="licenses_licenseTitleClick"
              onClick={() => handleLicenseClick(record)}
            >
              {getLicenseTypeDisplayed(text, licenses)}
            </span>
            <Tooltip
              title={LICENSE_LABELS.LICENSE_TYPE_PREFIX(licenseVersion)}
              placement="right"
              arrow
            >
              <span data-testid="licenses_licenseVersionIcon" className="licenses-license-version-icon">
                <Icon name="info" size="small" />
              </span>
            </Tooltip>
          </span>
        );
      }
    },
    {
      title: () => (
        <span className="licenses-table-header-title">
          <Title size="small" bold>
            {LICENSE_UI_TEXT.PURCHASED}
          </Title>
        </span>
      ),
      dataIndex: "purchased",
      key: "purchased",
      width: "20%",
      render: (text, record) => {
        const overuseState = getLicenseOveruseState(record.overusestartedon);
        const isSuspended = record.state && record.state.toLowerCase() !== LICENSE_STATUS.ACTIVE;

        const badgeConfig = overuseState === LICENSE_STATUS.GRACE_PERIOD || overuseState === LICENSE_STATUS.EXPIRED
          ? {
              tooltipTitle: LICENSE_UI_TEXT.LICENSE_OVERUSED_TAG,
              tooltipMessage: licensesMessage.LICENSE_OVERUSE_MESSAGE,
              badgeFillColor: "orange",
              badgeTextColor: "white",
              badgeText: LICENSE_UI_TEXT.LICENSE_OVERUSE_TAG
            }
          : isSuspended
          ? {
              tooltipTitle: LICENSE_UI_TEXT.LICENSES_SUSPENDED_TAG,
              tooltipMessage: licensesMessage.LICENSE_SUSPENDED_MESSAGE,
              badgeFillColor: "yellow",
              badgeText: LICENSE_UI_TEXT.LICENSES_SUSPENDED_TAG
            }
          : null;

        return (
          <div className="licenses-purchased-cell">
            <span>{text}</span>
            {badgeConfig && (
              <Tooltip
                placement="top"
                title={
                  <div>
                    <Body size="small" bold viewport="desktop">
                      {badgeConfig.tooltipTitle}
                    </Body>
                    <Body size="small" viewport="desktop">
                      {badgeConfig.tooltipMessage}
                    </Body>
                  </div>
                }
                arrow
                slotProps={{
                  tooltip: {
                    sx: {
                      backgroundColor: "white",
                      border: "1px solid black",
                      "& .MuiTooltip-arrow": { backgroundColor: "white" }
                    }
                  }
                }}
              >
                <span>
                  <Badge fillColor={badgeConfig.badgeFillColor}>
                    <Body size="small" bold color={badgeConfig.badgeTextColor}>
                      {badgeConfig.badgeText}
                    </Body>
                  </Badge>
                </span>
              </Tooltip>
            )}
          </div>
        );
      }
    },
    {
      title: () => (
        <span className="licenses-table-header-title">
          <Title size="small" bold>
            {LICENSE_UI_TEXT.USED}
          </Title>
        </span>
      ),
      dataIndex: "used",
      key: "used",
      width: "20%"
    },
    {
      title: () => (
        <span className="licenses-table-header-title">
          <Title size="small" bold>
            {LICENSE_UI_TEXT.AVAILABLE}
          </Title>
        </span>
      ),
      dataIndex: "available",
      key: "available",
      width: "20%"
    },
    {
      title: () => (
        <span className="licenses-table-header-title">
          <Title size="small" bold>
            {LICENSE_UI_TEXT.SITES}
          </Title>
        </span>
      ),
      dataIndex: "sites",
      key: "sites",
      width: "20%"
    }
  ];
  const handleLicenseClick = (record) => {
    dispatch({
      type: LICENSE_ACTION_TYPES.SET_SELECTED_LICENSE_RECORD,
      payload: { ...record, billingAccount: selectedBillingAcc || firstBillingAcc }
    });
    navigate("/licensedetails");
  };

  const LicenseListingTable = CustomHOCTable();
  return (
    <div className="licenses-page-container">
      <Breadcrumbs surface="light">
        <BreadcrumbItem>
          {isGracePeriodExpired ? (
            <span className="license-details-disabled-text">{LICENSE_UI_TEXT.SETTINGS}</span>
          ) : (
            <Link data-testid="licenses_settingsLink" to="/settings" className="breadcrums-item_link">
              {LICENSE_UI_TEXT.SETTINGS}
            </Link>
          )}
        </BreadcrumbItem>
        <BreadcrumbItem>{LICENSE_UI_TEXT.LICENSES}</BreadcrumbItem>
      </Breadcrumbs>
      <div className="licenses-page-title-wrapper">
        <Title size="large">{LICENSE_UI_TEXT.LICENSES}</Title>
      </div>
      <div className="licenses-dropdown-wrapper">
        <DropdownSelect
          errorText={LICENSE_UI_TEXT.SELECT_BILLING_ACCOUNT}
          error={false}
          disabled={false}
          readOnly={false}
          inlineLabel={false}
          width={isMobile ? "100%" : DROPDOWN_DEFAULT_WIDTH}
          onChange={handleBillingAccountChange}
        >
          {billingAccounts?.length > 0 ? (
            billingAccounts.map((account, index) => (
              <option key={index} value={account}>
                {LICENSE_UI_TEXT.BILLING_ACCOUNT_PREFIX}{account}
              </option>
            ))
          ) : (
            <option>{LICENSE_UI_TEXT.NO_BILLING_ACCOUNTS}</option>
          )}
        </DropdownSelect>
      </div>

      <div className="licenses-table-wrapper">
        <LicenseListingTable
          showSearch={false}
          showTitle={false}
          columns={columns}
          dataList={dataList}
          rowKey="id"
          //   onChange={handleChange}
          //   locale={customNoDataText}
          addDetailsButton={false}
          noFilter={true}
          //   loading={loading}
        />
      </div>
    </div>
  );
}

export default Licenses;
